'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Mail, Lock, Sparkles, ArrowRight, Shield, Star } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validate = () => {
    if (!email.trim()) return 'Please enter your email address.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email address.';
    if (!password) return 'Please enter your password.';
    if (password.length < 6) return 'Password must be at least 6 characters.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setLoading(true);
    try {
      await signIn(email.trim(), password);
      router.push('/home-screen');
      router.refresh();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.toLowerCase().includes('invalid login credentials') || msg.toLowerCase().includes('invalid credentials')) {
        setError('Invalid email or password. Please try again.');
      } else if (msg.toLowerCase().includes('email not confirmed')) {
        setError('Please verify your email before logging in.');
      } else {
        setError(msg || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Mandala Background */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-10 animate-rotate-slow"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='200' cy='200' r='190' fill='none' stroke='%23D4AF37' stroke-width='1'/%3E%3Ccircle cx='200' cy='200' r='150' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3Ccircle cx='200' cy='200' r='110' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3Ccircle cx='200' cy='200' r='70' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3Ccircle cx='200' cy='200' r='35' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3C/svg%3E")`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
          }}
        />
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-saffron/15 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-gold/10 blur-3xl" />
      </div>

      {/* Card */}
      <div className="relative z-10 w-full max-w-md animate-fade-in-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link href="/home-screen" className="inline-flex flex-col items-center gap-3 focus-ring rounded-xl p-2">
            <div className="w-16 h-16 rounded-full saffron-gradient flex items-center justify-center shadow-xl glow-saffron animate-float">
              <span className="text-3xl diya-glow" role="img" aria-label="Diya lamp">🪔</span>
            </div>
            <span className="font-display text-3xl font-bold text-ivory tracking-tight">Yagnick</span>
          </Link>
          <p className="mt-2 text-ivory/60 text-sm font-body">Sacred Rituals, Verified Priests</p>
        </div>

        <div className="glass-dark rounded-3xl p-8 shadow-2xl border border-gold/30">
          {/* Header */}
          <div className="text-center mb-7">
            <div className="inline-flex items-center gap-2 bg-gold/20 border border-gold/30 text-gold text-xs font-medium px-3 py-1.5 rounded-full mb-3 tracking-wider uppercase">
              <Sparkles size={11} aria-hidden="true" />
              Welcome Back
            </div>
            <h1 className="font-display text-2xl font-bold text-ivory">Sign In to Yagnick</h1>
            <p className="text-ivory/60 text-sm mt-1 font-body">Continue your sacred journey</p>
          </div>

          {/* Demo credentials hint */}
          <div className="mb-6 bg-gold/10 border border-gold/25 rounded-2xl px-4 py-3 text-center">
            <p className="text-gold/80 text-xs font-body">
              <span className="font-semibold">Demo:</span> demo@yagnick.com / demo1234
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-5 bg-red-900/30 border border-red-500/40 rounded-2xl px-4 py-3 animate-scale-in" role="alert" aria-live="polite">
              <p className="text-red-300 text-sm font-body">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" noValidate aria-label="Sign in form">
            {/* Email */}
            <div>
              <label htmlFor="login-email" className="block text-ivory/80 text-sm font-medium mb-2 font-body">
                Email Address
              </label>
              <div className="relative">
                <Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gold/60 pointer-events-none" aria-hidden="true" />
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="you@example.com"
                  className="w-full bg-white/8 border border-gold/25 hover:border-gold/50 focus:border-gold text-ivory placeholder-ivory/30 rounded-2xl pl-11 pr-4 py-3.5 text-sm font-body outline-none transition-all duration-200 focus:bg-white/12"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="login-password" className="text-ivory/80 text-sm font-medium font-body">
                  Password
                </label>
                <Link href="/forgot-password" className="text-gold/70 hover:text-gold text-xs font-body transition-colors focus-ring rounded">
                  Forgot Password?
                </Link>
              </div>
              <div className="relative">
                <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gold/60 pointer-events-none" aria-hidden="true" />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder="Enter your password"
                  className="w-full bg-white/8 border border-gold/25 hover:border-gold/50 focus:border-gold text-ivory placeholder-ivory/30 rounded-2xl pl-11 pr-12 py-3.5 text-sm font-body outline-none transition-all duration-200 focus:bg-white/12"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-ivory/40 hover:text-gold transition-colors focus-ring rounded"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full saffron-gradient text-white font-semibold py-4 rounded-2xl btn-glow flex items-center justify-center gap-2 text-base mt-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 focus-ring"
              aria-busy={loading}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight size={18} aria-hidden="true" />
                </>
              )}
            </button>
          </form>

          {/* Register link */}
          <p className="text-center text-ivory/50 text-sm mt-6 font-body">
            New to Yagnick?{' '}
            <Link href="/register" className="text-gold hover:text-saffron font-semibold transition-colors focus-ring rounded">
              Create Account
            </Link>
          </p>
        </div>

        {/* Trust indicators */}
        <div className="flex items-center justify-center gap-4 mt-6">
          <div className="flex items-center gap-1.5 text-ivory/40 text-xs font-body">
            <Shield size={12} aria-hidden="true" />
            <span>Secure & Encrypted</span>
          </div>
          <div className="w-1 h-1 rounded-full bg-ivory/20" aria-hidden="true" />
          <div className="flex items-center gap-1.5 text-ivory/40 text-xs font-body">
            <Star size={12} aria-hidden="true" />
            <span>12,400+ Rituals Performed</span>
          </div>
        </div>

        <p className="text-center text-ivory/30 text-xs mt-3 font-body">
          🙏 Connecting devotees with verified priests across India
        </p>
      </div>
    </div>
  );
}
